import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router';
import { useBlogTrans } from '../blogHooks/useBlogTrans';
import { useBlogContext } from './BlogProvider';

const BlogJsonld = observer(
    ({
        type,
        title,
        description,
        image,
        publishedAt,
    }: {
        type:
            | 'BlogPosting' // 글 상세
            | 'Blog' // 글 목록
            | 'Organization' // 조직 (홈페이지에 추가)
            | 'WebSite'; // 홈페이지
        title?: string;
        description?: string;
        image?: string;
        publishedAt?: string;
    }) => {
        const baseUrl = 'https://retirein40s.com';
        const { lang } = useBlogContext();
        const { trans } = useBlogTrans();
        const location = useLocation();

        if (!lang) {
            return null;
        }

        const pathname = location.pathname || '';
        const fullUrl = `${baseUrl}${pathname}`;

        const serviceTitle = trans('blogIndexTitle');

        // 태그 내에 들어갈 땐 공백을 정리해줌
        const refinedDescription = description ? description.replace(/\s+/g, ' ') : '';

        const jsonld = useMemo(() => {
            if (type === 'WebSite') {
                return {
                    '@context': 'https://schema.org',
                    '@type': 'WebSite',
                    name: serviceTitle,
                    url: fullUrl,
                    description: description || trans('blogIntroDesc'),
                    termsOfService: `https://retirein40s.com/${lang}/terms`,
                    privacyPolicy: `https://retirein40s.com/${lang}/privacy`,
                };
            } else if (type === 'Organization') {
                return {
                    '@context': 'https://schema.org',
                    '@type': 'Organization',
                    name: serviceTitle,
                    url: baseUrl,
                    logo: 'https://static.retirein40s.com/images/blog_logo/r40_logo_600.png',
                };
            } else if (type === 'Blog') {
                return {
                    '@context': 'https://schema.org',
                    '@type': 'Blog',
                    name: `${title} | ${serviceTitle}`,
                    url: fullUrl,
                    description: refinedDescription,
                };
            } else if (type === 'BlogPosting') {
                return {
                    '@context': 'https://schema.org',
                    '@type': type,
                    headline: title,
                    image: image || '',
                    publisher: {
                        '@type': 'Organization',
                        name: serviceTitle,
                        logo: {
                            '@type': 'ImageObject',
                            url: 'https://static.retirein40s.com/images/blog_logo/r40_logo_600.png',
                        },
                    },
                    datePublished: publishedAt,
                    description: refinedDescription,
                    url: fullUrl,
                    mainEntityOfPage: {
                        '@type': 'WebPage',
                        '@id': fullUrl,
                    },
                };
            } else {
                return null;
            }
        }, [type, title, image, publishedAt, refinedDescription, fullUrl, serviceTitle]);

        const jsonldString = useMemo(() => {
            if (!jsonld || _.isEmpty(jsonld)) {
                return null;
            }

            return JSON.stringify(
                _.extend(
                    {
                        '@context': 'https://schema.org',
                    },
                    jsonld,
                ),
            );
        }, [jsonld]);

        if (!jsonldString) {
            return null;
        }

        return (
            <Helmet>
                <script type="application/ld+json">{jsonldString}</script>
            </Helmet>
        );
    },
);

export default BlogJsonld;
